import React, { useState, useMemo } from 'react'
import {
    Box,
    IconButton,
    Paper,
    Typography,
    Grid,
    TextField,
    InputAdornment,
    Skeleton,
    CardActionArea,
    Tooltip
} from '@mui/material'
/** TYPES */
import { Service } from '@/types/product'
import { CartItem } from '@/types/pos'
/** SERVICES */
import { fetchServices } from '@/hooks/product'
/** ICONS */
import ServiceIcon from '@mui/icons-material/BuildOutlined'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'
import SearchIcon from '@mui/icons-material/Search'
import { NumericFormat } from 'react-number-format'

interface POSServicesProps {
    addItemToCart: (items: CartItem[]) => void
}

const POSServices = ({ addItemToCart }: POSServicesProps) => {
    const [services, setServices] = useState<Service[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [searchTerm, setSearchTerm] = useState('')

    const getServices = React.useCallback(async () => {
        setLoading(true)
        try {
            const fetchedServices = await fetchServices()
            setServices(fetchedServices)
        } catch (error) {
            console.error('Failed to fetch services:', error)
        } finally {
            setLoading(false)
        }
    }, [])

    React.useEffect(() => {
        getServices()
    }, [getServices])

    // Filter services based on search
    const filteredServices = useMemo(() => {
        return services.filter(s => 
            ((s.label || s.name).toLowerCase().includes(searchTerm.toLowerCase())) ||
            s.description?.toLowerCase().includes(searchTerm.toLowerCase())
        )
    }, [services, searchTerm])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Top Bar with Search */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <TextField
                    placeholder="Үйлчилгээ хайх..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ width: 300, bgcolor: 'background.paper' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Grid Container */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 2,
                }}
            >
                {loading
                    ? Array.from(new Array(8)).map((_, index) => <ServiceSkeleton key={index} />)
                    : filteredServices.map((service) => (
                          <ServiceItem 
                            key={service.id} 
                            service={service} 
                            onAddToCart={addItemToCart} 
                          />
                      ))}
            </Box>
        </Box>
    )
}

const ServiceItem = ({ service, onAddToCart }: { service: Service; onAddToCart: (items: CartItem[]) => void }) => {
    const handleAdd = () => {
        onAddToCart([{
            type: 'service',
            name: service.name,
            label: service.label,
            id: service.id,
            total: service.price ?? 0,
            quantity: 1,
            price: service.price ?? 0,
        }])
    }

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                    borderColor: 'rgba(0,0,0,0.12)',
                },
            }}
        >
            <CardActionArea onClick={handleAdd}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 3,
                    background: (theme) => theme.palette.mode === 'light' 
                        ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
                        : 'rgba(255,255,255,0.05)',
                }}>
                    <ServiceIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.8 }} />
                </Box>

                <Box sx={{ padding: 2, height: 100, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 1 }}>
                        {service.label}
                    </Typography>
                    <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {service.description || 'Тайлбар байхгүй'}
                    </Typography>
                </Box>
            </CardActionArea>

            <Box sx={{
                padding: '8px 16px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Typography variant="h6" color="primary.dark" sx={{ fontWeight: 800 }}>
                    <NumericFormat 
                        value={service.price} 
                        displayType={'text'} 
                        thousandSeparator={true} 
                        suffix={' ₮'}
                    />
                </Typography>
                <Tooltip title="Сагсанд нэмэх">
                    <IconButton 
                        size="small"
                        color="primary" 
                        onClick={handleAdd}
                        sx={{ 
                            bgcolor: 'primary.light', 
                            color: 'white',
                            '&:hover': { bgcolor: 'primary.main' }
                        }}
                    >
                        <AddShoppingCartIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    )
}

const ServiceSkeleton = () => (
    <Paper sx={{ borderRadius: 3, padding: 2 }}>
        <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="circular" width={32} height={32} />
        </Box>
    </Paper>
)

export default POSServices